import React, { useMemo } from 'react';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { copyToClipboard, OutlinedIcons } from '@marageti/z4-lib';
import { SalesRecording } from '@marageti/z4-sdk/lib/travel';
import { Link } from 'react-router-dom';
import { SalesRecordingType, SalesRecordingStatus } from '@marageti/z4-sdk/lib/travel/SalesRecording';
import { formatDateSold, salesRecordingTypeLabel } from '../../../utils/sales';

type Props = {
  sales: SalesRecording[],
  totalSales: string | number | undefined,
  totalCommission: string | number | undefined,
};

type Row = {
  id: string;
  recordedOn: string;
  tripId: string;
  traveler: string;
  agent: string;
  type: string;
  amount: string;
  commission: string;
  status: string;
  salesRecordingDetailUrl: string | null;
};

const InvoiceTable = ({
  sales,
  totalCommission,
  totalSales,
}: Props) => {
  const {
    OutlinedDocumentDuplicateIcon,
  } = OutlinedIcons;
  const { enqueueSnackbar } = useSnackbar();
  const rows: Row[] = useMemo(() => (sales.map((sale) => {
    const getSalesRecordingDetails = () => {
      switch (sale.salesRecordingType) {
        case SalesRecordingType.Adjustment:
        case SalesRecordingType.General:
        case SalesRecordingType.CommissionAfterTravel:
        case SalesRecordingType.PlanningFee:
          return `/trip-matches/${sale.tripMatchId}/sales/${sale.id}`;
        case SalesRecordingType.PartnerRepeat:
        case SalesRecordingType.TravelerReferral:
          return `/sales/repeat-referral/${sale.id}`;
        default:
          return null;
      }
    };

    const getSalesRecordingStatus = () => {
      switch (sale.status) {
        case SalesRecordingStatus.InvoicedStatus:
          return 'Invoiced';
        case SalesRecordingStatus.VerifiedStatus:
          return 'Approved';
        case SalesRecordingStatus.SoldStatus:
        default:
          return '';
      }
    };

    const firstTraveler = sale.travelers?.[0];
    const salesRecordingDetailUrl = getSalesRecordingDetails();
    const status = getSalesRecordingStatus();

    return {
      id: sale.id,
      recordedOn: formatDateSold(sale.createdAt),
      tripId: sale.referenceId,
      traveler: firstTraveler?.fullName,
      agent: sale.assignedToName,
      type: salesRecordingTypeLabel[sale.salesRecordingType],
      amount: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(sale.price.amountUSD),
      commission: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(sale.commission.amountUSD),
      status,
      salesRecordingDetailUrl,
    };
  })), [sales]);

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Recorded</TableCell>
            <TableCell>Trip ID</TableCell>
            <TableCell>Traveler</TableCell>
            <TableCell>Agent</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Amount</TableCell>
            <TableCell>Commission</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              hover
              key={row.id}
              component={Link}
              to={row.salesRecordingDetailUrl || '#'}
              sx={{
                cursor: 'pointer',
                textDecoration: 'none',
              }}
            >
              <TableCell sx={{ minWidth: '130px' }}>{row.recordedOn}</TableCell>
              <TableCell sx={{ minWidth: '130px' }}>
                {row.tripId && (
                  <Box component="span" display="flex" alignItems="center">
                    {row.tripId}
                    <Tooltip title="Copy Trip ID to clipboard" placement="top">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          if (row.tripId) {
                            e.stopPropagation();
                            copyToClipboard(row.tripId);
                            enqueueSnackbar('Copied!', { variant: 'success' });
                          }
                        }}
                        aria-label="copy trip ID"
                        sx={{ fontSize: '1.1rem' }}
                      >
                        <OutlinedDocumentDuplicateIcon color="primary" fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                {!row.tripId && '-'}
              </TableCell>
              <TableCell>{row.traveler}</TableCell>
              <TableCell>{row.agent}</TableCell>
              <TableCell sx={{ minWidth: '130px' }}>{row.type}</TableCell>
              <TableCell sx={{ color: row.amount.startsWith('-') ? 'error.main' : undefined }}>{row.amount}</TableCell>
              <TableCell sx={{ color: row.commission.startsWith('-') ? 'error.main' : undefined }}>{row.commission}</TableCell>
              <TableCell><span style={{ fontStyle: 'italic' }}>{row.status}</span></TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell sx={{ fontFamily: 'captionBold' }}>Totals</TableCell>
            <TableCell sx={{ fontFamily: 'captionBold' }}>{totalSales}</TableCell>
            <TableCell sx={{ fontFamily: 'captionBold' }}>{totalCommission}</TableCell>
            <TableCell />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

export default InvoiceTable;
